
/* eslint-disable vue/no-mutating-props */
// `target` (state.primary) and `referral` are orchestrator-owned; mutated in
// place like the V2 wizard's step targets.
import { COPY, MEDICARE_PARTS_OPTIONS } from './copy'
import CrmSearch from './CrmSearch.vue'
import ReturningClientFields from './ReturningClientFields.vue'
import ReferralFields from './ReferralFields.vue'
import ReferredByFields from './ReferredByFields.vue'
import UpcomingApptNotice from './UpcomingApptNotice.vue'
import { showClientState } from './logic'
import { isOpenState } from '~/util/open-states'
import { isKeptFormerIchra, KEPT_FORMER_ICHRA_LABEL, KEPT_FORMER_ICHRA_TOOLTIP } from '~/util/former-ichra'

export default {
    components: { CrmSearch, ReturningClientFields, ReferralFields, ReferredByFields, UpcomingApptNotice },
    props: {
        target: { type: Object, required: true },
        referral: { type: Object, required: true },
        excludeIds: { type: Array, default: () => [] },
        upcoming: { type: Object, default: null },
        // #679 Phase 3 Task 3: state.schedule, plumbed down from AdminScheduler
        // so the consultation-type pill row can read/write schedule.aep and
        // schedule.reason (mirrors the schedule prop already threaded to
        // MethodTimeCard).
        schedule: { type: Object, required: true },
        // Same resolved allowed-methods AdminScheduler already computes for
        // MethodTimeCard (returningAllowedMethods / newClientAllowedMethods) —
        // reused here rather than re-deriving the referral's paying flags.
        allowedMethods: { type: Array, default: null },
        // The AEP season window (isAEPScheduling) — gates the consultation
        // type, whose answer sets schedule.aep.
        aepVisible: { type: Boolean, default: false },
        // #679 polish: /client-allowed-methods/'s authoritative enrolled
        // boolean (null until it resolves, and always null for new clients).
        // The Enrolled badge requires it — survey-only + returning alone
        // over-claims for returning DPC/fullserve clients who were never
        // enrolled with us.
        enrolled: { type: Boolean, default: null },
        // #727: the open states (FL/RI) from the settings endpoint.
        openStates: { type: Array, default: () => [] },
        // #727/#730: a RETURNING client the open-state rule governs (the backend's
        // answer) -- their phone allowance must not read as a paying partner.
        openStateOnly: { type: Boolean, default: false },
    },
    data () {
        return { COPY, KEPT_FORMER_ICHRA_LABEL, KEPT_FORMER_ICHRA_TOOLTIP }
    },
    computed: {
        keptFormerIchra () {
            return isKeptFormerIchra(this.target.record)
        },
        // #727/#730: the open-state rule governs this client -- a new one whose
        // referral resolved to non-paying in an open state, or a returning one
        // the backend judged so.
        openStateRule () {
            if (this.target.returning === true) return this.openStateOnly
            return this.target.returning === false &&
                showClientState(this.referral) && isOpenState(this.referral.state, this.openStates)
        },
        partsLabel () {
            const parts = this.target.record && this.target.record.medicare_parts
            const opt = MEDICARE_PARTS_OPTIONS.find(o => o.value === parts)
            return opt ? opt.text : (parts || '')
        },
        recordEmail () {
            const r = this.target.record
            return (r && (r.email || r.work_email)) || ''
        },
        // Paying when the resolved tier allows a call — for a returning client
        // that's returningAllowedMethods including 'phone'; for a new client
        // newClientAllowedMethods already folds in groupPaying/
        // groupFinancialAdvisor/DC-internal to the same effect. Reusing
        // allowedMethods here avoids re-deriving those referral flags.
        payingBadge () {
            return Array.isArray(this.allowedMethods) && this.allowedMethods.includes('phone')
        },
        isSurveyOnlyMethods () {
            return Array.isArray(this.allowedMethods) && this.allowedMethods.length === 1 && this.allowedMethods[0] === 'survey'
        },
        // Controller ruling (#679 review, Finding 2) — see the template
        // comment above: split by target.returning, not one wording for both.
        // Polish pass: "Enrolled" additionally requires the endpoint's real
        // enrolled boolean; a returning survey-only client who was never
        // enrolled (DPC/fullserve) falls through to "Survey only".
        enrolledBadge () {
            return this.isSurveyOnlyMethods && this.target.returning === true && this.enrolled === true
        },
        surveyOnlyBadge () {
            return this.isSurveyOnlyMethods && !this.enrolledBadge
        },
    },
    methods: {
        setReturning (v) {
            if (this.target.returning === v) return
            this.$set(this.target, 'returning', v)
            this.$set(this.target, 'record', null)
            this.$emit('reset')
        },
        setReason (v) {
            if (this.schedule.reason === v) return
            this.$set(this.schedule, 'reason', v)
            // The type answer IS the aep answer (Dien 2026-09-01).
            const aep = v === 'plan_review'
            if (this.schedule.aep !== aep) {
                this.$set(this.schedule, 'aep', aep)
                // Mirrors the old setAep guards: a pre-picked method must not
                // survive a flip that removes it from the offer (video is
                // never offered under aep; survey only exists under aep).
                if (aep && this.schedule.method === 'video') {
                    this.$set(this.schedule, 'method', null)
                }
                if (!aep && this.schedule.method === 'survey') {
                    this.$set(this.schedule, 'method', null)
                }
            }
        },
    },
}
